/* eslint-disable import/prefer-default-export */
import isActiveSale from "../Pricing/isActiveSale";
import {
  Price,
  ProductDetailPageResult,
  Templates,
} from "../ProductDetailPageResult";
import { GroupInStockOptions } from "./SchemaHelpers";
import getLowestDisplayPrice from "~/common/Product/getLowestDisplayPrice";
import getMinBuyForQuantityDropdown from "../Pricing/AddToCart/getMinBuyForQuantityDropdown";
import { User } from "~/common/Store/models/User";

type PriceSchema = {
  "@type": string;
  price: number;
  priceCurrency: string;
  priceType?: string;
  validForMemberTier?: {
    "@type": string;
    "@id": string;
  };
};

const getGoogleViewPrice = (product: ProductDetailPageResult, user: User) => {
  let price = getLowestDisplayPrice(product, user, true);

  if (product.minimumBuy > 1 || product.mustBuy > 1) {
    const updatedStartBuy = getMinBuyForQuantityDropdown({
      minBuy: product.minimumBuy,
      mustBuy: product.mustBuy,
    });

    price *= updatedStartBuy;
  }
  return price;
};

const isP5Valid = (price: Price, hasActiveSale: boolean) => {
  const p5 = price.minimumAdvertisedPriceOverride?.priceLevel5;
  const p4 = price.minimumAdvertisedPriceProperties?.price;
  if (p5 === undefined || p4 === undefined) return false;
  if (p5 === 0 || p5 > price.price) return false;
  if (p5 > p4 && p4 !== 0) return false;
  if (p5 > price.salePrice && hasActiveSale) return false;
  return true;
};

const isP4Invalid = (price: Price, hasActiveSale: boolean) => {
  const p4 = price.minimumAdvertisedPriceProperties?.price;
  if (!p4 || p4 === 0) return true;
  if (p4 > price.price) return true;
  if (p4 > price.salePrice && hasActiveSale) return true;
  return false;
};

const getPriceSchemaArray = (price: Price) => {
  // Note that this follows the same logic for checking if P5 is valid as the Google Feed (https://tfs.clarkinc.biz/DefaultCollection/Webstaurantstore.com/_wiki/wikis/Webstaurantstore.com.wiki/630/Google-Feed)
  const hasActiveSalePrice = !!price.salePrice && isActiveSale(price);
  const shouldShowP5 =
    isP5Valid(price, hasActiveSalePrice) &&
    isP4Invalid(price, hasActiveSalePrice);

  const priceSchemaArray: PriceSchema[] = [];

  if (hasActiveSalePrice) {
    priceSchemaArray.push({
      "@type": "UnitPriceSpecification",
      price: price.salePrice,
      priceCurrency: "USD",
    });
    priceSchemaArray.push({
      "@type": "UnitPriceSpecification",
      priceType: "https://schema.org/StrikethroughPrice",
      price: price.price,
      priceCurrency: "USD",
    });
  } else {
    priceSchemaArray.push({
      "@type": "UnitPriceSpecification",
      price: price.price,
      priceCurrency: "USD",
    });
  }

  if (shouldShowP5) {
    priceSchemaArray.push({
      "@type": "UnitPriceSpecification",
      price: price.minimumAdvertisedPriceOverride.priceLevel5,
      priceCurrency: "USD",
      validForMemberTier: {
        "@type": "MemberProgramTier",
        "@id": "https://www.webstaurantstore.com/plus/#plus-member",
      },
    });
  }

  return priceSchemaArray;
};

const getAvailability = (
  product: ProductDetailPageResult,
  groupInStockOptions?: GroupInStockOptions
) => {
  const {
    showEverlivingProductPage,
    groupingAttributes,
    noticesAndWarnings,
  } = product;
  let availability = "https://schema.org/InStock";
  const hasSpecialOrder =
    groupInStockOptions?.hasSpecialOrder ?? noticesAndWarnings?.isSpecialOrder;

  // If it's a discontinued page/product
  if (showEverlivingProductPage) {
    availability = "https://schema.org/Discontinued";
  }
  // If it is a group master product
  else if (groupingAttributes.isGroupMaster) {
    if (
      (groupInStockOptions?.hasInStock && groupInStockOptions?.hasOutOfStock) ||
      groupInStockOptions?.hasExclusive ||
      hasSpecialOrder
    ) {
      availability = "https://schema.org/LimitedAvailability";
    } else if (!groupInStockOptions?.hasInStock) {
      availability = "https://schema.org/OutOfStock";
    }
  }
  // If it is a single product
  else if (product.customerExclusivity?.disallowNonMemberToPurchase) {
    availability = "https://schema.org/LimitedAvailability";
  } else if (!product.isInStock) {
    availability = "https://schema.org/OutOfStock";
  } else if (hasSpecialOrder ?? false) {
    availability = "https://schema.org/LimitedAvailability";
  }
  return availability;
};

export const getOfferSchema = (
  domain: string,
  product: ProductDetailPageResult,
  user: User,
  isGoogleView: boolean,
  groupInStockOptions?: GroupInStockOptions
) => {
  const {
    template,
    isConsumable,
    link,
    showEverlivingProductPage,
    hasDropShip,
    noticesAndWarnings,
  } = product;

  const hasSpecialOrder =
    groupInStockOptions?.hasSpecialOrder ?? noticesAndWarnings?.isSpecialOrder;

  return {
    "@type": "Offer",
    availability: getAvailability(product, groupInStockOptions),
    ...(!showEverlivingProductPage
      ? {
          url: domain + link,
        }
      : undefined),
    itemCondition:
      template === Templates.OutletTemplate
        ? "https://schema.org/DamagedCondition"
        : "https://schema.org/NewCondition",
    ...(!hasDropShip && !hasSpecialOrder && !showEverlivingProductPage
      ? {
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "US",
            ...(isConsumable || template === Templates.OutletTemplate
              ? {
                  returnPolicyCategory:
                    "https://schema.org/MerchantReturnNotPermitted",
                }
              : {
                  returnPolicyCategory:
                    "https://schema.org/MerchantReturnFiniteReturnWindow",
                  merchantReturnDays: 30,
                  returnMethod: "https://schema.org/ReturnByMail",
                }),
          },
        }
      : undefined),
    ...(isGoogleView
      ? { price: getGoogleViewPrice(product, user), priceCurrency: "USD" }
      : { priceSpecification: getPriceSchemaArray(product.price) }),
  };
};
